import { useState, useEffect } from 'react'
import type { Program } from '../../lib/types'
import { apiGetJson, apiPostJson } from '../../lib/apiClient'
import { Download } from 'lucide-react'

export default function ProgramsTab() {
  const [programs, setPrograms] = useState<Program[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchPrograms()
  }, [])

  const fetchPrograms = async () => {
    try {
      const resp = await apiGetJson<{ success: boolean; data: Program[] }>('/api/programs')
      setPrograms((resp?.data || []) as Program[])
    } catch (error) {
      console.error('Error fetching programs:', error)
    } finally {
      setLoading(false)
    }
  }

  // Note: For high traffic, RPC is better to avoid race conditions.
  const handleSimpleTrack = async (program: Program, type: 'view' | 'download') => {
    try {
      const resp = await apiPostJson<{ success: boolean; data?: any }>('/api/programs/track', { id: program.id, type })
      const update = resp?.data && typeof resp.data === 'object' ? (resp.data as any) : null

      if (update) {
        setPrograms(prev => prev.map(p => (p.id === program.id ? { ...p, ...update } : p)))
      }
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <div className="animate-fade-in">
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-6 animate-pulse">
              <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-2/3 mb-6"></div>
              <div className="flex space-x-3">
                <div className="h-10 bg-gray-200 dark:bg-gray-800 rounded flex-1"></div>
                <div className="h-10 bg-gray-200 dark:bg-gray-800 rounded flex-1"></div>
              </div>
            </div>
          ))}
        </div>
      ) : programs.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-900 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-300">Chưa có chương trình nào được chia sẻ.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.map((program) => (
            <div key={program.id} className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 hover:shadow-md transition-shadow duration-200 flex flex-col h-full">
              <div className="p-6 flex-1">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 line-clamp-2" title={program.name}>
                    {program.name}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-6 line-clamp-3">
                  {program.description || 'Không có mô tả'}
                </p>

                <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-300 mb-6">
                  {program.version && (
                    <div className="flex items-center bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                      <span className="font-medium mr-1">v</span>{program.version}
                    </div>
                  )}
                  <div className="flex items-center">
                    <Download className="h-4 w-4 mr-1" />
                    {program.download_count?.toLocaleString() || 0}
                  </div>
                </div>
              </div>

              <div className="p-6 pt-0 mt-auto grid grid-cols-1 gap-3">
                <a
                  href={program.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleSimpleTrack(program, 'download')}
                  className="flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-950 focus:ring-amber-500 transition-colors w-full"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Tải về
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
